import { useViewport } from "@faire/react/viewport/useViewport";
import { Add } from "@faire/slate-icons/Add";
import { ArrowLeft } from "@faire/slate-icons/ArrowLeft";
import { Check } from "@faire/slate-icons/Check";
import { Close } from "@faire/slate-icons/Close";
import { Button } from "@faire/slate/components/button";
import { IconButton } from "@faire/slate/components/icon-button";
import { TextInput } from "@faire/slate/components/text-input";
import { tsv } from "@faire/slate/lib/tsv";
import {
  LayoutGroup,
  motion,
  type Variants,
  useReducedMotion,
} from "framer-motion";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import { UnfavoriteConfirmationModal } from "./UnfavoriteConfirmationModal";

const POPOVER_WIDTH = 320;
const POPOVER_MAX_HEIGHT = 356;
const VIEWPORT_MARGIN = 12;
const ANCHOR_OFFSET = 8;
const COLLECTION_LIST_HORIZONTAL_PADDING = 16;
const SELECTION_INDICATOR_SIZE = 32;
const SELECTION_INDICATOR_CENTER_FROM_POPOVER_LEFT =
  POPOVER_WIDTH -
  COLLECTION_LIST_HORIZONTAL_PADDING -
  SELECTION_INDICATOR_SIZE / 2;
const UNFAVORITE_FEEDBACK_DELAY_MS = 400;
const POPOVER_ENTRY_EXIT_TRANSITION = {
  duration: 0.15,
  ease: "easeOut",
} as const;
const POPOVER_FLOW_LAYOUT_TRANSITION = {
  duration: 0.25,
  ease: [0.45, 0, 0.55, 1],
} as const;
const COLLECTION_COVER_IMAGE_TRANSITION = {
  duration: 0.14,
  ease: "easeOut",
} as const;
const COLLECTION_THUMBNAIL_VARIANTS: Variants = {
  idle: {
    scale: 1,
  },
  add: {
    scale: [1, 1.1, 0.94, 1],
    transition: {
      duration: 0.3,
      ease: "easeOut",
      times: [0, 0.32, 0.68, 1],
    },
  },
  remove: {
    scale: [1, 0.9, 1.06, 1],
    transition: {
      duration: 0.3,
      ease: "easeOut",
      times: [0, 0.32, 0.68, 1],
    },
  },
};
const REDUCED_MOTION_COLLECTION_THUMBNAIL_VARIANTS: Variants = {
  idle: { scale: 1 },
  add: { scale: 1, transition: { duration: 0 } },
  remove: { scale: 1, transition: { duration: 0 } },
};

export interface CollectionImageTransition {
  backgroundImageUrl: string | undefined;
  coverImageUrl: string;
  direction: "add" | "remove";
  id: number;
}

export interface FavoriteCollection {
  id: string;
  name: string;
  imageUrl?: string;
  imageTransition?: CollectionImageTransition;
}

export type PopoverAnchorLocation = "product-card" | "heart";

interface FavoriteCollectionPickerProps {
  anchorElement: HTMLElement;
  anchorLocation: PopoverAnchorLocation;
  closeOnCollectionSelect: boolean;
  collections: FavoriteCollection[];
  productImageUrl: string;
  selectedCollectionIds: string[];
  onClose: () => void;
  onCollectionImageTransitionComplete: (
    collectionId: string,
    transitionId: number
  ) => void;
  onCreateCollection: (collectionName: string) => void;
  onExitComplete: () => void;
  onToggleCollection: (collectionId: string) => void;
  open: boolean;
}

export const FavoriteCollectionPicker: React.FC<
  FavoriteCollectionPickerProps
> = ({
  anchorElement,
  anchorLocation,
  closeOnCollectionSelect,
  collections,
  productImageUrl,
  selectedCollectionIds,
  onClose,
  onCollectionImageTransitionComplete,
  onCreateCollection,
  onExitComplete,
  onToggleCollection,
  open,
}) => {
  const { isMobile } = useViewport();
  const [isUnfavoriteConfirmationOpen, setIsUnfavoriteConfirmationOpen] =
    useState(false);
  const [isCreatingCollection, setIsCreatingCollection] = useState(false);
  const [collectionName, setCollectionName] = useState("");
  const unfavoriteFeedbackTimeoutRef = useRef<number | null>(null);

  const clearUnfavoriteFeedbackTimeout = () => {
    if (unfavoriteFeedbackTimeoutRef.current === null) {
      return;
    }
    window.clearTimeout(unfavoriteFeedbackTimeoutRef.current);
    unfavoriteFeedbackTimeoutRef.current = null;
  };

  const closeAfterUnfavoriteFeedback = () => {
    clearUnfavoriteFeedbackTimeout();
    unfavoriteFeedbackTimeoutRef.current = window.setTimeout(() => {
      unfavoriteFeedbackTimeoutRef.current = null;
      onClose();
    }, UNFAVORITE_FEEDBACK_DELAY_MS);
  };

  const handleToggleCollectionClick = (collectionId: string) => {
    clearUnfavoriteFeedbackTimeout();
    const removesProductFromOtherCollections =
      collectionId === "all-favorites" &&
      selectedCollectionIds.includes(collectionId) &&
      selectedCollectionIds.length > 1;
    if (removesProductFromOtherCollections) {
      setIsUnfavoriteConfirmationOpen(true);
      return;
    }
    onToggleCollection(collectionId);
    const removesLastFavorite =
      collectionId === "all-favorites" &&
      selectedCollectionIds.includes(collectionId) &&
      selectedCollectionIds.length === 1;
    if (closeOnCollectionSelect || removesLastFavorite) {
      closeAfterUnfavoriteFeedback();
    }
  };

  useEffect(
    () => () => {
      if (unfavoriteFeedbackTimeoutRef.current !== null) {
        window.clearTimeout(unfavoriteFeedbackTimeoutRef.current);
      }
    },
    []
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (isUnfavoriteConfirmationOpen) {
          setIsUnfavoriteConfirmationOpen(false);
        } else if (isCreatingCollection) {
          setIsCreatingCollection(false);
        } else {
          onClose();
        }
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isCreatingCollection, isUnfavoriteConfirmationOpen, onClose]);

  useEffect(() => {
    if (!open && (isMobile || isUnfavoriteConfirmationOpen)) {
      onExitComplete();
    }
  }, [isMobile, isUnfavoriteConfirmationOpen, onExitComplete, open]);

  if (!open && isMobile) {
    return null;
  }

  const pickerContent = isCreatingCollection ? (
    <NewCollectionForm
      collectionName={collectionName}
      key="new-collection-form"
      onBack={() => setIsCreatingCollection(false)}
      onCollectionNameChange={setCollectionName}
      onSubmit={() => {
        const trimmedCollectionName = collectionName.trim();
        if (!trimmedCollectionName) {
          return;
        }
        onCreateCollection(trimmedCollectionName);
      }}
      productImageUrl={productImageUrl}
    />
  ) : (
    <PickerContent
      collections={collections}
      key="collection-picker"
      onCollectionImageTransitionComplete={onCollectionImageTransitionComplete}
      onStartCreateCollection={() => setIsCreatingCollection(true)}
      onToggleCollection={handleToggleCollectionClick}
      selectedCollectionIds={selectedCollectionIds}
    />
  );
  const confirmationModal = (
    <UnfavoriteConfirmationModal
      imageUrl={productImageUrl}
      onCancel={() => setIsUnfavoriteConfirmationOpen(false)}
      onConfirm={() => {
        onToggleCollection("all-favorites");
        onClose();
      }}
      open={isUnfavoriteConfirmationOpen}
    />
  );

  if (isMobile) {
    return (
      <>
        {isUnfavoriteConfirmationOpen
          ? null
          : createPortal(
              <div className={mobileOverlayStyle.cls()} onMouseDown={onClose}>
                <div
                  aria-label="Save to collections"
                  aria-modal="true"
                  className={mobileDrawerStyle.cls()}
                  data-test-id="favorites-drawer"
                  role="dialog"
                  onMouseDown={(event) => event.stopPropagation()}
                >
                  {isCreatingCollection ? null : (
                    <div className={drawerHeaderStyle.cls()}>
                      <div aria-hidden className={drawerHandleStyle.cls()} />
                      <p className="type-fs-subheading-medium">
                        Save to collections
                      </p>
                      <IconButton
                        aria-label="Close"
                        Icon={Close}
                        onClick={onClose}
                        size="xSmall"
                        variant="tertiary"
                      />
                    </div>
                  )}
                  {pickerContent}
                </div>
              </div>,
              document.body
            )}
        {confirmationModal}
      </>
    );
  }

  return (
    <>
      {isUnfavoriteConfirmationOpen ? null : (
        <DesktopFavoriteCollectionPopover
          anchorElement={anchorElement}
          anchorLocation={anchorLocation}
          maxHeight={POPOVER_MAX_HEIGHT}
          onClose={onClose}
          onExitComplete={onExitComplete}
          open={open}
        >
          {pickerContent}
        </DesktopFavoriteCollectionPopover>
      )}
      {confirmationModal}
    </>
  );
};

interface DesktopFavoriteCollectionPopoverProps {
  anchorElement: HTMLElement;
  anchorLocation: PopoverAnchorLocation;
  children: React.ReactNode;
  maxHeight: number;
  onClose: () => void;
  onExitComplete: () => void;
  open: boolean;
}

const DesktopFavoriteCollectionPopover: React.FC<
  DesktopFavoriteCollectionPopoverProps
> = ({
  anchorElement,
  anchorLocation,
  children,
  maxHeight,
  onClose,
  onExitComplete,
  open,
}) => {
  const popoverRef = useRef<HTMLDivElement>(null);
  const shouldReduceMotion = useReducedMotion();
  const [position, setPosition] = useState(() =>
    getPopoverPosition(anchorElement, anchorLocation, maxHeight)
  );

  const updatePosition = useCallback(() => {
    const nextPosition = getPopoverPosition(
      anchorElement,
      anchorLocation,
      maxHeight
    );
    setPosition((currentPosition) =>
      currentPosition.bottom === nextPosition.bottom &&
      currentPosition.left === nextPosition.left &&
      currentPosition.top === nextPosition.top &&
      currentPosition.transformOrigin === nextPosition.transformOrigin
        ? currentPosition
        : nextPosition
    );
  }, [anchorElement, anchorLocation, maxHeight]);

  useLayoutEffect(() => {
    updatePosition();
    const handleScroll = (event: Event) => {
      if (!popoverRef.current?.contains(event.target as Node)) {
        onClose();
      }
    };
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", handleScroll, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [onClose, updatePosition]);

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      if (!popoverRef.current?.contains(event.target as Node)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [onClose]);

  return createPortal(
    <LayoutGroup>
      <motion.div
        animate={open ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.98 }}
        ref={popoverRef}
        aria-label="Save to collections"
        className={desktopPopoverPositionerStyle.cls()}
        data-test-id="favorites-popover"
        initial={shouldReduceMotion ? false : { opacity: 0, scale: 0.98 }}
        onAnimationComplete={() => {
          if (!open) {
            onExitComplete();
          }
        }}
        role="dialog"
        style={{
          bottom: position.bottom,
          left: position.left,
          pointerEvents: open ? "auto" : "none",
          top: position.top,
          transformOrigin: position.transformOrigin,
          willChange: "transform, opacity",
          width: POPOVER_WIDTH,
        }}
        transition={
          shouldReduceMotion ? { duration: 0 } : POPOVER_ENTRY_EXIT_TRANSITION
        }
      >
        <motion.div
          className={desktopPopoverSurfaceStyle.cls()}
          layout={shouldReduceMotion ? false : true}
          style={{
            boxShadow: "0 2px 12px 0 rgba(0, 0, 0, 0.15)",
            maxHeight,
          }}
          transition={
            shouldReduceMotion
              ? { duration: 0 }
              : { layout: POPOVER_FLOW_LAYOUT_TRANSITION }
          }
        >
          {children}
        </motion.div>
      </motion.div>
    </LayoutGroup>,
    document.body
  );
};

const getPopoverPosition = (
  anchorElement: HTMLElement,
  anchorLocation: PopoverAnchorLocation,
  maxHeight: number
) => {
  const anchorRect = anchorElement.getBoundingClientRect();
  const anchorBottom =
    anchorLocation === "heart"
      ? anchorRect.bottom
      : anchorRect.top + anchorRect.width;
  const preferredTop = anchorBottom + ANCHOR_OFFSET;
  const fitsBelow =
    preferredTop + maxHeight + VIEWPORT_MARGIN <= window.innerHeight;
  const top = fitsBelow ? preferredTop : undefined;
  const preferredBottom = window.innerHeight - anchorRect.top + ANCHOR_OFFSET;
  const maxBottom = window.innerHeight - VIEWPORT_MARGIN - maxHeight;
  const bottom = fitsBelow ? undefined : Math.min(preferredBottom, maxBottom);
  const rightAlignedLeft = anchorRect.right - POPOVER_WIDTH;
  const shouldAlignLeft = rightAlignedLeft < VIEWPORT_MARGIN;
  const anchorCenter = anchorRect.left + anchorRect.width / 2;
  const heartAlignedLeft =
    anchorCenter - SELECTION_INDICATOR_CENTER_FROM_POPOVER_LEFT;
  let preferredLeft = rightAlignedLeft;
  if (anchorLocation === "heart") {
    preferredLeft = heartAlignedLeft;
  } else if (shouldAlignLeft) {
    preferredLeft = anchorRect.left;
  }
  const maxLeft = window.innerWidth - POPOVER_WIDTH - VIEWPORT_MARGIN;
  const left = Math.max(VIEWPORT_MARGIN, Math.min(preferredLeft, maxLeft));
  let horizontalTransformOrigin = "right";
  if (anchorLocation === "heart") {
    horizontalTransformOrigin = `${Math.max(
      0,
      Math.min(anchorCenter - left, POPOVER_WIDTH)
    )}px`;
  } else if (shouldAlignLeft) {
    horizontalTransformOrigin = "left";
  }

  return {
    bottom,
    left,
    top,
    transformOrigin: `${horizontalTransformOrigin} ${
      fitsBelow ? "top" : "bottom"
    }`,
  };
};

interface PickerContentProps {
  collections: FavoriteCollection[];
  onCollectionImageTransitionComplete: (
    collectionId: string,
    transitionId: number
  ) => void;
  selectedCollectionIds: string[];
  onStartCreateCollection: () => void;
  onToggleCollection: (collectionId: string) => void;
}

const PickerContent: React.FC<PickerContentProps> = ({
  collections,
  onCollectionImageTransitionComplete,
  selectedCollectionIds,
  onStartCreateCollection,
  onToggleCollection,
}) => {
  const collectionListRef = useRef<HTMLDivElement>(null);
  const [showScrollFade, setShowScrollFade] = useState(false);
  const shouldReduceMotion = useReducedMotion();
  const collectionCount = collections.length;
  const updateScrollFade = useCallback((collectionList: HTMLDivElement) => {
    const hasMoreContent =
      collectionList.scrollHeight -
        collectionList.scrollTop -
        collectionList.clientHeight >
      1;
    setShowScrollFade(hasMoreContent);
  }, []);

  useLayoutEffect(() => {
    const collectionList = collectionListRef.current;
    if (!collectionList) {
      return;
    }
    updateScrollFade(collectionList);
    const resizeObserver = new ResizeObserver(() => {
      updateScrollFade(collectionList);
    });
    resizeObserver.observe(collectionList);
    return () => resizeObserver.disconnect();
  }, [collectionCount, updateScrollFade]);

  return (
    <motion.div
      animate={{ opacity: 1 }}
      className={pickerContentStyle.cls()}
      initial={shouldReduceMotion ? false : { opacity: 0 }}
      layout={shouldReduceMotion ? false : "position"}
      transition={
        shouldReduceMotion
          ? { duration: 0 }
          : {
              ...POPOVER_ENTRY_EXIT_TRANSITION,
              layout: POPOVER_FLOW_LAYOUT_TRANSITION,
            }
      }
    >
      <div className={scrollAreaStyle.cls()}>
        <div
          className={collectionListStyle.cls()}
          onScroll={(event) => updateScrollFade(event.currentTarget)}
          ref={collectionListRef}
        >
          {collections.map((collection, index) => (
            <div key={collection.id}>
              {index === 1 ? (
                <div className="border-fs-border-muted mb-(--slate-spacing-group-large) border-t" />
              ) : null}
              <CollectionRow
                collection={collection}
                isSelected={selectedCollectionIds.includes(collection.id)}
                onCollectionImageTransitionComplete={
                  onCollectionImageTransitionComplete
                }
                onToggleCollection={onToggleCollection}
              />
            </div>
          ))}
        </div>
        <div
          aria-hidden
          className={scrollFadeStyle.cls({ visible: showScrollFade })}
        />
      </div>
      <div className={newCollectionFooterStyle.cls()}>
        <button
          aria-label="Create a new collection"
          className={collectionRowStyle.cls()}
          onClick={onStartCreateCollection}
          type="button"
        >
          <NewCollectionThumbnail />
          <p className="type-fs-paragraph-medium min-w-0 flex-1 truncate">
            New collection
          </p>
          <SelectionIndicator isSelected={false} />
        </button>
      </div>
    </motion.div>
  );
};

interface NewCollectionFormProps {
  collectionName: string;
  productImageUrl: string;
  onBack: () => void;
  onCollectionNameChange: (collectionName: string) => void;
  onSubmit: () => void;
}

const NewCollectionForm: React.FC<NewCollectionFormProps> = ({
  collectionName,
  productImageUrl,
  onBack,
  onCollectionNameChange,
  onSubmit,
}) => {
  const [showCollectionNameError, setShowCollectionNameError] = useState(false);
  const shouldReduceMotion = useReducedMotion();

  return (
    <motion.form
      className={newCollectionFormStyle.cls()}
      layout={shouldReduceMotion ? false : "position"}
      transition={
        shouldReduceMotion ? { duration: 0 } : POPOVER_FLOW_LAYOUT_TRANSITION
      }
      onSubmit={(event) => {
        event.preventDefault();
        if (!collectionName.trim()) {
          setShowCollectionNameError(true);
          return;
        }
        onSubmit();
      }}
    >
      <div className={newCollectionFormHeaderStyle.cls()}>
        <IconButton
          aria-label="Back to collections"
          Icon={ArrowLeft}
          onClick={onBack}
          size="xSmall"
          type="button"
          variant="tertiary"
        />
      </div>
      <div className={newCollectionFormBodyStyle.cls()}>
        <div
          className={newCollectionProductImageStyle.cls()}
          style={{
            boxShadow: "-1px 2px 4px 0 rgba(0, 0, 0, 0.12)",
          }}
        >
          <img
            alt=""
            className="size-full object-cover"
            src={productImageUrl}
          />
          <ProductImageScrim />
        </div>
        <div className="w-full">
          <TextInput
            autoFocus
            error={showCollectionNameError}
            label="Collection name"
            onChange={(event) => {
              onCollectionNameChange(event.target.value);
              if (event.target.value.trim()) {
                setShowCollectionNameError(false);
              }
            }}
            placeholder="Holiday cards 2027"
            value={collectionName}
          />
        </div>
      </div>
      <div className={newCollectionFormFooterStyle.cls()}>
        <Button fullWidth type="submit">
          Create
        </Button>
      </div>
    </motion.form>
  );
};

interface CollectionRowProps {
  collection: FavoriteCollection;
  isSelected: boolean;
  onCollectionImageTransitionComplete: (
    collectionId: string,
    transitionId: number
  ) => void;
  onToggleCollection: (collectionId: string) => void;
}

const CollectionRow: React.FC<CollectionRowProps> = ({
  collection,
  isSelected,
  onCollectionImageTransitionComplete,
  onToggleCollection,
}) => (
  <button
    aria-label={`${isSelected ? "Remove from" : "Add to"} ${collection.name}`}
    aria-pressed={isSelected}
    className={collectionRowStyle.cls()}
    onClick={() => onToggleCollection(collection.id)}
    type="button"
  >
    <CollectionThumbnail
      collection={collection}
      onTransitionComplete={onCollectionImageTransitionComplete}
    />
    <p className="type-fs-paragraph-medium min-w-0 flex-1 truncate">
      {collection.name}
    </p>
    <SelectionIndicator isSelected={isSelected} />
  </button>
);

const SelectionIndicator: React.FC<{ isSelected: boolean }> = ({
  isSelected,
}) => (
  <span aria-hidden className={selectionIndicatorStyle.cls({ isSelected })}>
    {isSelected ? (
      <Check
        color="var(--slate-color-icon-primary-inverse)"
        height="16px"
        width="16px"
      />
    ) : (
      <Add height="16px" width="16px" />
    )}
  </span>
);

const ProductImageScrim: React.FC = () => (
  <div
    aria-hidden
    className="pointer-events-none absolute inset-0"
    style={{ backgroundColor: "rgba(0, 0, 0, 0.05)" }}
  />
);

const CollectionThumbnail: React.FC<{
  collection: FavoriteCollection;
  onTransitionComplete: (collectionId: string, transitionId: number) => void;
}> = ({ collection, onTransitionComplete }) => {
  const shouldReduceMotion = useReducedMotion();
  const imageTransition = collection.imageTransition;
  const isAddingCoverImage = imageTransition?.direction === "add";
  const coverImageInitialOpacity = isAddingCoverImage ? 0 : 1;
  const coverImageInitialScale = isAddingCoverImage ? 0.8 : 1;

  return (
    <motion.div
      animate={imageTransition?.direction ?? "idle"}
      className={collectionThumbnailStyle.cls()}
      initial="idle"
      key={imageTransition?.id ?? `${collection.id}-idle`}
      onAnimationComplete={() => {
        if (imageTransition) {
          onTransitionComplete(collection.id, imageTransition.id);
        }
      }}
      variants={
        shouldReduceMotion
          ? REDUCED_MOTION_COLLECTION_THUMBNAIL_VARIANTS
          : COLLECTION_THUMBNAIL_VARIANTS
      }
    >
      {imageTransition ? (
        <>
          {imageTransition.backgroundImageUrl ? (
            <img
              alt=""
              className={collectionImageStyle.cls()}
              src={imageTransition.backgroundImageUrl}
            />
          ) : null}
          <motion.img
            animate={
              isAddingCoverImage
                ? { opacity: 1, scale: 1 }
                : { opacity: 0, scale: 0.8 }
            }
            alt=""
            className={collectionImageStyle.cls()}
            initial={
              shouldReduceMotion
                ? false
                : {
                    opacity: coverImageInitialOpacity,
                    scale: coverImageInitialScale,
                  }
            }
            src={imageTransition.coverImageUrl}
            transition={
              shouldReduceMotion
                ? { duration: 0 }
                : COLLECTION_COVER_IMAGE_TRANSITION
            }
          />
        </>
      ) : collection.imageUrl ? (
        <img
          alt=""
          className={collectionImageStyle.cls()}
          src={collection.imageUrl}
        />
      ) : null}
      <ProductImageScrim />
    </motion.div>
  );
};

const NewCollectionThumbnail: React.FC = () => (
  <div className="relative size-10 shrink-0">
    <div
      className={newCollectionBackTileWrapperStyle.cls()}
      style={{
        height: 27.391,
        left: 12.61,
        top: 6.024,
        width: 27.391,
      }}
    >
      <div
        className={newCollectionBackTileStyle.cls()}
        style={{ height: 24.923, width: 24.923 }}
      />
    </div>
    <div
      className={newCollectionFrontTileStyle.cls()}
      style={{
        boxShadow: "-0.554px 1.108px 2.215px 0 rgba(0, 0, 0, 0.12)",
        height: 31.431,
        left: 0,
        top: 4.284,
        width: 31.431,
      }}
    />
  </div>
);

const desktopPopoverPositionerStyle = tsv({
  base: "fixed z-50",
});

const desktopPopoverSurfaceStyle = tsv({
  base: [
    "bg-fs-surface-primary rounded-fs-container-default",
    "flex w-full overflow-hidden",
  ],
});

const pickerContentStyle = tsv({
  base: "flex min-h-0 w-full flex-1 flex-col",
});

const newCollectionFormStyle = tsv({
  base: "flex h-full w-full flex-col",
});

const newCollectionFormHeaderStyle = tsv({
  base: [
    "flex h-[72px] shrink-0 items-center",
    "px-(--slate-spacing-element-large)",
  ],
});

const newCollectionFormBodyStyle = tsv({
  base: [
    "flex h-[200px] shrink-0 flex-col items-center",
    "gap-(--slate-spacing-element-medium) px-(--slate-spacing-element-large)",
  ],
});

const newCollectionFormFooterStyle = tsv({
  base: [
    "flex h-[84px] shrink-0 items-end",
    "px-(--slate-spacing-element-large) pt-(--slate-spacing-element-large) pb-(--slate-dimensions-5)",
  ],
});

const newCollectionProductImageStyle = tsv({
  base: [
    "border-fs-border-muted bg-fs-surface-tertiary",
    "rounded-fs-component-default relative size-32 shrink-0 overflow-hidden border",
  ],
});

const scrollAreaStyle = tsv({
  base: "relative min-h-0 flex-1 overflow-hidden",
});

const collectionListStyle = tsv({
  base: [
    "flex h-full flex-col overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
    "gap-(--slate-spacing-group-large) px-(--slate-spacing-element-large) pt-(--slate-spacing-element-large)",
  ],
});

const scrollFadeStyle = tsv({
  base: [
    "pointer-events-none absolute right-0 bottom-0 left-0 h-10",
    "via-fs-surface-primary/60 to-fs-surface-primary bg-gradient-to-b from-transparent",
  ],
  variants: {
    visible: {
      true: "opacity-100",
      false: "opacity-0",
    },
  },
});

const newCollectionFooterStyle = tsv({
  base: "shrink-0 p-(--slate-spacing-element-large)",
});

const collectionRowStyle = tsv({
  base: [
    "group focusable interactive rounded-fs-component-default flex min-h-10 w-full items-center text-left",
    "gap-(--slate-spacing-group-medium)",
  ],
});

const selectionIndicatorStyle = tsv({
  base: [
    "flex size-8 shrink-0 items-center justify-center",
    "rounded-fs-component-round transition-colors",
  ],
  variants: {
    isSelected: {
      true: [
        "bg-fs-action-surface-default text-fs-icon-primary-inverse",
        "group-hover:bg-fs-action-surface-hover",
      ],
      false: [
        "border-fs-action-border-subdued bg-fs-action-surface-inverse border",
        "group-hover:border-fs-action-border-hover group-hover:bg-fs-action-surface-inverse-hover",
      ],
    },
  },
});

const collectionThumbnailStyle = tsv({
  base: [
    "border-fs-border-muted bg-fs-surface-tertiary",
    "rounded-fs-component-default relative size-10 shrink-0 overflow-hidden border",
  ],
});

const collectionImageStyle = tsv({
  base: "absolute inset-0 size-full object-cover",
});

const newCollectionBackTileWrapperStyle = tsv({
  base: "absolute flex items-center justify-center",
});

const newCollectionBackTileStyle = tsv({
  base: [
    "border-fs-border-muted bg-fs-surface-tertiary",
    "rounded-fs-component-default rotate-6 border border-dashed",
  ],
});

const newCollectionFrontTileStyle = tsv({
  base: [
    "border-fs-border-muted bg-fs-surface-tertiary",
    "rounded-fs-component-default absolute border border-dashed",
  ],
});

const mobileOverlayStyle = tsv({
  base: [
    "bg-fs-surface-mask fixed inset-0 z-50",
    "flex items-end justify-center",
  ],
});

const mobileDrawerStyle = tsv({
  base: [
    "bg-fs-surface-primary rounded-t-fs-container-default",
    "shadow-elevation-medium flex max-h-[85vh] w-full flex-col overflow-hidden",
  ],
});

const drawerHeaderStyle = tsv({
  base: [
    "border-fs-border-muted relative flex items-center justify-between border-b",
    "gap-(--slate-spacing-group-default) px-(--slate-spacing-element-large) py-(--slate-spacing-element-medium)",
  ],
});

const drawerHandleStyle = tsv({
  base: [
    "bg-fs-surface-tertiary absolute top-(--slate-spacing-element-xsmall) left-1/2",
    "rounded-fs-component-round h-1 w-10 -translate-x-1/2",
  ],
});
